import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Trash2, Tag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { clientsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '-');

export const ClientPricesDialog = ({ client, open, onOpenChange }) => {
  const { isSlave } = useAuth();
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    setLoading(true);
    clientsApi.productPrices(client.id)
      .then((res) => setPrices(res.data))
      .catch(() => toast.error('Errore nel caricamento dei prezzi memorizzati'))
      .finally(() => setLoading(false));
  }, [open, client]);

  const handleDelete = async (productId) => {
    try {
      await clientsApi.deleteProductPrice(client.id, productId);
      setPrices((prev) => prev.filter((p) => p.product_id !== productId));
      toast.success('Prezzo memorizzato eliminato');
    } catch {
      toast.error('Impossibile eliminare il prezzo');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-prices-dialog" className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] font-semibold flex items-center gap-2">
            <Tag className="w-4 h-4" /> Prezzi memorizzati · {client?.nickname || client?.name}
          </DialogTitle>
          <DialogDescription>
            Ultimo prezzo a cui hai venduto ogni prodotto a questo cliente. Viene precompilato automaticamente nei nuovi DDT.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : prices.length === 0 ? (
          <p data-testid="client-prices-empty" className="py-10 text-center text-sm text-muted-foreground">
            Nessun prezzo memorizzato: verrà salvato automaticamente al prossimo DDT per questo cliente.
          </p>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-swiss">Codice</TableHead>
                  <TableHead className="table-header-swiss">Descrizione</TableHead>
                  <TableHead className="table-header-swiss text-right">Prezzo</TableHead>
                  <TableHead className="table-header-swiss">Ultimo DDT</TableHead>
                  {!isSlave() && <TableHead className="table-header-swiss w-[50px]" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {prices.map((p) => (
                  <TableRow key={p.product_id} data-testid={`client-price-row-${p.product_id}`}>
                    <TableCell className="font-['JetBrains_Mono'] text-xs">{p.product_code}</TableCell>
                    <TableCell className="text-sm">{p.description}</TableCell>
                    <TableCell data-testid={`client-price-value-${p.product_id}`} className="text-right font-['JetBrains_Mono'] font-semibold">{eur(p.price)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.ddt_number ? `N. ${p.ddt_number}/${p.ddt_year}` : '-'} · {fmtDate(p.updated_at)}
                    </TableCell>
                    {!isSlave() && (
                      <TableCell>
                        <Button data-testid={`delete-client-price-${p.product_id}`} variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.product_id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientPricesDialog;
